import { Button, Card, CardBody, Col, Container, Label, Row } from 'reactstrap';
import Breadcrumbs from '../../../coreComponents/breadcrumbs';
import CommonCardHeader from '../../../coreComponents/CommonCardHeader';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import SimpleModal from '../../uiKits/modal/basicModal/SimpleModal';
import AjaxSourcedData from '../../formsAndTable/tables/dataTables/dataSource/AjaxSourcedData';
import { AssistantExamDataCreate } from '../../../types/Tables';
import {
  addCalendarStopPeriods,
  deleteCalendarStopPeriods,
  getCalendarStopPeriods,
  getPrompts,
} from '../../../services/api';
import CreateExamForm from './CreateExamForm';
import {
  useAssistantCreateExamMutation,
  useAssistantGetExamQuery,
} from '../../../reduxToolkit/reducers/usersSlice';
import {
  AssistantExamData,
  getAssistantExamListTableColumns,
} from '../../../data/formsAndTable/tables/dataTables/DataSource';
import QuestionList from '../examDetails/[id]/QuestionList';
import { useAppSelector } from '../../../reduxToolkit/hooks';
import { UserRole } from '../../../types/Forms';
import { skipToken } from '@reduxjs/toolkit/query';
import { useLocation, useNavigate } from 'react-router-dom';
import { SlotGrid } from './TimeSlotsGrid';
import { Period, SlotCell, SlotStatus } from '../../../types/Exams';
import { buildSlotCellsFromPeriods } from './BuildSlotCellsFromPeriods';
import { cellToPeriod, markCellsInRange } from './slotHelpers';
import TimePickerWithLimitedTime from '../../formsAndTable/forms/formWidgets/datePicker/timePicker/TimePickerWithLimitedTime';
import Swal from 'sweetalert2';
import { Primary } from '../../../constants';
import { useSocket } from '../../../hooks/useSocket';
import { parseQuestionsField } from '../../../lib/questionsParse';
import { NoDataComponent } from '../../formsAndTable/forms/formControls/NoDataComponent';
import Loader from '../../../layout/Loader';
const formDefaults: Partial<AssistantExamDataCreate> = {
  name: '',
  courseId: 0,
  lessonId: 0,
  prompt: { ru: '', uk: '' },
};

const AssistantCreateExam = () => {
  const { role, id: userId } = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenQuestions, setIsOpenQuestions] = useState(false);
  const [calendarStopPeriods, setCalendarStopPeriods] = useState<Period[]>([]);
  const [questions, setQuestions] = useState<
    | {
        questions: string[];
        type: 'stringArray';
      }
    | {
        type: 'objectArray';
        questions: { questionNum: number; questionText: string }[];
      }
    | null
  >(null);
  const [createAssistantExam] = useAssistantCreateExamMutation();
  const [createExamFormDefaults, setCreateExamFormDefaults] =
    useState<Partial<AssistantExamDataCreate>>(formDefaults);
  const [slots, setSlots] = useState<{ dates: string[] | null; cells: SlotCell[] | null }>({
    dates: null,
    cells: null,
  });
  const [periodStart, setPeriodStart] = useState<Date>(new Date());
  const [periodEnd, setPeriodEnd] = useState<Date>(new Date());
  const [isOpenStopEdit, setIsOpenStopEdit] = useState(false);
  const args = role == null ? skipToken : role === UserRole.STUDENT && userId ? { userId } : {};
  const {
    data: assistantExamData,
    refetch,
    isLoading,
    isFetching,
  } = useAssistantGetExamQuery(args);
  const [examDataForTable, setExamDataForTable] = useState<AssistantExamData[]>([]);
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(now.getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const location = useLocation();
  const navigate = useNavigate();
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const fetchStopPeriods = async () => {
    const periods: Period[] = await getCalendarStopPeriods();
    setCalendarStopPeriods(periods);

    const cells = buildSlotCellsFromPeriods(periods, {
      workStart: '09:30',
      workEnd: '17:30',
      stepMin: 15,
      timeZone: 'Europe/Kyiv',
    });

    // Get the local days you want as columns (e.g., next 7 days)
    const days = Array.from(new Set(cells.map((c) => c.date))).sort();
    setSlots({
      dates: days,
      cells,
    });
  };
  useEffect(() => {
    fetchStopPeriods();
  }, []);
  const rebuildSlots = (periods: Period[]) => {
    const cells = buildSlotCellsFromPeriods(periods, {
      workStart: '09:30',
      workEnd: '17:30',
      stepMin: 15,
      timeZone: 'Europe/Kyiv',
    });
    const days = Array.from(new Set(cells.map((c) => c.date))).sort();
    setSlots({ dates: days, cells });
  };
  const cellKey = (c: SlotCell) => `${c.date}_${c.time}`;
  const getPeriodId = (id: string | number | undefined) => {
    if (id == null) return undefined;
    if (typeof id === 'number') return String(id);
    const str = String(id);
    return str.includes('_') ? str.split('_')[1] : str;
  };
  const cellClickHander = async (cell: SlotCell) => {
    if (savingKey && savingKey === cellKey(cell)) return;

    if (cell.status !== 'available' && cell.status !== 'closed') return;

    const period = cellToPeriod(cell, 15); // returns { start: Date, end: Date, author }
    const prevCells = slots?.cells ?? [];
    const toggledStatus: SlotStatus = cell.status === 'available' ? 'closed' : 'available';

    const optimistic = markCellsInRange(
      prevCells,
      period.start.toISOString(),
      period.end.toISOString(),
      toggledStatus,
      'admin@admin.com',
    );
    setSlots((prev) => ({ ...prev, cells: optimistic }));
    setSavingKey(cellKey(cell));

    try {
      if (cell.status === 'available') {
        const created = await addCalendarStopPeriods({
          start: period.start.toISOString(),
          end: period.end.toISOString(),
          author: 'admin@admin.com',
        });
        // If server normalized range, reconcile with server’s start/end
        const s = created?.start ?? period.start.toISOString();
        const e = created?.end ?? period.end.toISOString();
        const pid = created?.id ?? created?.periodId ?? null;
        setSlots((prev) => ({
          ...prev,
          cells: markCellsInRange(
            prev.cells ?? [],
            s,
            e,
            'closed',
            'admin@admin.com',
            pid ? { id: pid } : undefined,
          ),
        }));
      } else {
        const periodId = getPeriodId(cell.id);
        if (!periodId) {
          // Revert because we optimistically changed UI
          setSlots((cur) => ({ ...cur, cells: prevCells }));
          return;
        }

        await deleteCalendarStopPeriods(periodId, cell.date, cell.time); // expect 204/ok

        setSlots((prev) => ({
          ...prev,
          cells: markCellsInRange(
            prev.cells ?? [],
            period.start.toISOString(),
            period.end.toISOString(),
            'available',
          ),
        }));
      }
    } catch (err) {
      // REVERT ON ERROR
      setSlots((cur) => ({ ...cur, cells: prevCells }));
    } finally {
      setSavingKey(null);
    }
  };
  useEffect(() => {
    if (assistantExamData) {
      const filtered = assistantExamData.filter(
        (el: AssistantExamData) => el.parentExamId === null,
      );
      setExamDataForTable(filtered);
    }
  }, [assistantExamData]);
  useEffect(() => {
    const needRefresh = location.state?.refresh;
    if (!needRefresh) return;
    refetch();

    // 2) clear the flag so it doesn't refire on re-render
    navigate('.', { replace: true, state: {} });
  }, []);

  useEffect(() => {
    const getQuestionPrompt = async () => {
      const requests = await getPrompts();
      setCreateExamFormDefaults((prev) => {
        return {
          ...prev,
          prompt: requests[0].questionRequest,
        };
      });
    };
    getQuestionPrompt();
  }, []);

  const handleSeeQuestions = (data: AssistantExamData) => {
    const rawText = data.questions;
    // const questionList = rawText
    //   .split('\n') // split into lines
    //   .slice(1) // remove intro line
    //   .map((line) => line.trim()) // trim whitespace
    //   .filter((line) => line.length > 0); // remove empty lines

    const parsed = parseQuestionsField(rawText);

    // Ensure parsed always has a defined 'type' property
    if (parsed && (parsed.type === 'stringArray' || parsed.type === 'objectArray')) {
      setQuestions({
        type: parsed.type,
        questions: parsed.questions,
      } as
        | { type: 'stringArray'; questions: string[] }
        | { type: 'objectArray'; questions: { questionNum: number; questionText: string }[] });
    } else {
      setQuestions({
        type: 'stringArray',
        questions: Array.isArray(parsed) ? parsed : typeof parsed === 'string' ? [parsed] : [],
      });
    }
    setIsOpenQuestions(true);
  };
  const handleAddStopPeriod = async () => {
    if (!periodStart || !periodEnd) {
      return;
    }
    const plus15 = new Date(periodEnd.getTime() + 15 * 60 * 1000);
    const period = {
      start: periodStart,
      end: plus15.toISOString(),
    };

    try {
      const res = await addCalendarStopPeriods(period);
      if (res.id) {
        setCalendarStopPeriods((prev) => {
          const next = [
            ...prev,
            {
              id: res.id,
              start: res.start, // use server-normalized values
              end: res.end,
              author: res.author ?? 'admin@admin.com',
              duration:
                res.duration ??
                Math.round((new Date(res.end).getTime() - new Date(res.start).getTime()) / 60000),
            },
          ];
          // 2) rebuild grid from updated periods
          rebuildSlots(next);
          return next;
        });
        Swal.fire({
          title: 'Period added',
          icon: 'success',
          confirmButtonColor: Primary,
        });
      } else {
        Swal.fire({
          title: 'Error',
          icon: 'error',
          confirmButtonColor: Primary,
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        icon: 'error',
        confirmButtonColor: Primary,
      });
    } finally {
      setIsOpenStopEdit(false);
    }
  };
  const handleMonthChange = (m: number) => {
    if (m === 0) {
      setCurrentMonth(12);
      currentYear > 0 && setCurrentYear(currentYear - 1);
    } else if (m === 13) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(m);
    }
  };
  useSocket(
    'calendar:slot-taken',
    (payload: {
      start: string | Date; // ISO
      userId?: number;
    }) => {
      // payload: { start: string, end: string, examId: number, by: 'student' }
      // simplest: re-fetch periods so UI updates immediately
      fetchStopPeriods();
    },
  );

  return (
    <>
      <Breadcrumbs
        mainTitle={t('sidebar.examManage')}
        // parent={t('courses.generalTitle')}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CommonCardHeader title={t('courses.exam.examListCardTitle')} tag />
              <CardBody>
                <Row>
                  {/* {role !== UserRole.STUDENT && (
                    <Col sm="2" className="mb-2">
                      {' '}
                      <Button color="primary" onClick={() => setIsOpenModal(true)}>
                        {t('exams.createNewExam')}
                      </Button>
                    </Col>
                  )} */}

                  <AjaxSourcedData
                    data={examDataForTable}
                    columns={getAssistantExamListTableColumns(handleSeeQuestions, t, viewerTz)}
                    defaultSortFieldId="examDate"
                    defaultSortAsc={false}
                    noDataComponent={<NoDataComponent />}
                    loading={isLoading || isFetching}
                  />
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {slots.cells && slots.dates && (
          <Row>
            <Col sm="12">
              <Card>
                <CommonCardHeader title={t('exams.stopPeriodsTitle')} tag />
                <CardBody>
                  {/* <Row className="mb-3">
                    <Col sm="3">
                      <Button onClick={() => setIsOpenStopEdit(true)}>
                        {t('exams.addClosedSlot')}
                      </Button>
                    </Col>
                  </Row> */}

                  <SlotGrid
                    // dates={slots.dates}
                    cells={slots.cells}
                    onCellClick={cellClickHander}
                    currentMonth={currentMonth}
                    monthChange={handleMonthChange}
                    currentYear={currentYear}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
        {/* <SimpleModal isOpen={isOpenModal} close={() => setIsOpenModal(false)} size={'xl'}>
          <CreateExamForm
            defaults={createExamFormDefaults}
            close={() => setIsOpenModal(false)}
            createAssistantExam={createAssistantExam}
          />
        </SimpleModal> */}

        <SimpleModal isOpen={isOpenQuestions} close={() => setIsOpenQuestions(false)}>
          <QuestionList
            qs={
              questions ?? {
                type: 'stringArray',
                questions: [],
              }
            }
          />
          <Row className="mt-2 justify-content-center">
            <Col sm="auto">
              <Button
                type="button"
                color="primary"
                outline
                onClick={() => setIsOpenQuestions(false)}
              >
                {t('global.ok')}
              </Button>
            </Col>
          </Row>
        </SimpleModal>
        <SimpleModal isOpen={isOpenStopEdit} close={() => setIsOpenStopEdit(false)}>
          <Card>
            <CardBody>
              <Label className="w-full">
                {t('exams.slotStart')}
                <TimePickerWithLimitedTime
                  setDateTime={setPeriodStart}
                  disabledPeriods={calendarStopPeriods}
                />
              </Label>
              <Label className="w-full">
                {t('exams.slotEnd')}
                <TimePickerWithLimitedTime
                  setDateTime={setPeriodEnd}
                  isRange
                  rangeStart={periodStart}
                  disabledPeriods={calendarStopPeriods}
                />
              </Label>
              <Row className="pt-3">
                <Col sm="6">
                  <Button
                    type="button"
                    color="primary"
                    outline
                    onClick={handleAddStopPeriod}
                    className="w-100 mr-3"
                  >
                    {t('global.ok')}
                  </Button>
                </Col>

                <Col sm="6">
                  <Button
                    type="button"
                    color="secondary"
                    outline
                    onClick={() => setIsOpenStopEdit(false)}
                    className="w-100 ml-3"
                  >
                    {t('global.cancel')}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </SimpleModal>
      </Container>
    </>
  );
};
export default AssistantCreateExam;
